import { useState } from "react";
import {
  ChevronUp, ChevronDown, Trash2, Plus, Copy, Pencil, Image as ImageIcon,
  Type, Heading, MoreHorizontal, Award, Link as LinkIcon, Minus, Layout, Code,
} from "lucide-react";
import { BLOCK_LIBRARY, defaultBlock } from "@/lib/blockRenderer";

const ICONS = {
  header: Layout,
  hero: Heading,
  text: Type,
  image: ImageIcon,
  stats: Award,
  cta: LinkIcon,
  cta_row: LinkIcon,
  testimonial: Type,
  divider: Minus,
  footer: MoreHorizontal,
  raw: Code,
};

export default function BlockEditor({ blocks, onChange, disabled }) {
  const [editingId, setEditingId] = useState(null);
  const [pickerAt, setPickerAt] = useState(-1); // index after which to insert
  const [showAddTop, setShowAddTop] = useState(false);

  const update = (next) => onChange(next);

  const addAt = (idx, type) => {
    const blk = defaultBlock(type);
    const next = [...blocks];
    next.splice(idx, 0, blk);
    update(next);
    setPickerAt(-1);
    setShowAddTop(false);
    setEditingId(blk.id);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[j]] = [next[j], next[idx]];
    update(next);
  };

  const remove = (idx) => {
    if (!window.confirm("Remove this block?")) return;
    const next = blocks.filter((_, i) => i !== idx);
    update(next);
  };

  const duplicate = (idx) => {
    const src = blocks[idx];
    const copy = { ...src, id: defaultBlock(src.type).id, data: JSON.parse(JSON.stringify(src.data || {})) };
    const next = [...blocks];
    next.splice(idx + 1, 0, copy);
    update(next);
  };

  const patchBlock = (idx, patch) => {
    const next = [...blocks];
    next[idx] = { ...next[idx], data: { ...next[idx].data, ...patch } };
    update(next);
  };

  return (
    <div className="space-y-2" data-testid="block-editor">
      {/* top "add block" */}
      {!disabled && (
        <AddSlot
          open={showAddTop}
          onToggle={() => { setShowAddTop(!showAddTop); setPickerAt(-1); }}
          onPick={(t) => addAt(0, t)}
        />
      )}

      {blocks.map((blk, idx) => {
        const Icon = ICONS[blk.type] || Type;
        const isEditing = editingId === blk.id;
        return (
          <div key={blk.id}>
            <div
              className={`border rounded-md transition-all ${isEditing ? "bg-white" : "bg-[#FBFAF9] hover:bg-white"}`}
              style={{ borderColor: isEditing ? "var(--rahi-red)" : "var(--rahi-border)" }}
              data-testid={`block-${blk.type}`}
            >
              <div className="flex items-center gap-2 px-3 py-2">
                <Icon size={14} style={{ color: "var(--rahi-muted)" }}/>
                <span className="text-sm font-medium capitalize flex-1">{blk.type}</span>
                {!disabled && (
                  <div className="flex items-center gap-0.5">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="icon-btn" data-testid="block-move-up" title="Move up">
                      <ChevronUp size={14}/>
                    </button>
                    <button onClick={() => move(idx, 1)} disabled={idx === blocks.length - 1} className="icon-btn" data-testid="block-move-down" title="Move down">
                      <ChevronDown size={14}/>
                    </button>
                    <button onClick={() => duplicate(idx)} className="icon-btn" data-testid="block-duplicate" title="Duplicate">
                      <Copy size={13}/>
                    </button>
                    <button onClick={() => setEditingId(isEditing ? null : blk.id)} className="icon-btn" data-testid="block-edit" title="Edit">
                      <Pencil size={13}/>
                    </button>
                    <button onClick={() => remove(idx)} className="icon-btn text-red-600" data-testid="block-delete" title="Delete">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                )}
              </div>
              {isEditing && !disabled && (
                <div className="px-4 pb-4 pt-1 border-t" style={{ borderColor: "var(--rahi-border)" }}>
                  <BlockFields type={blk.type} data={blk.data || {}} onPatch={(p) => patchBlock(idx, p)}/>
                </div>
              )}
            </div>
            {!disabled && (
              <AddSlot
                open={pickerAt === idx}
                onToggle={() => { setPickerAt(pickerAt === idx ? -1 : idx); setShowAddTop(false); }}
                onPick={(t) => addAt(idx + 1, t)}
                small
              />
            )}
          </div>
        );
      })}

      {blocks.length === 0 && (
        <div className="text-center py-12 text-sm" style={{ color: "var(--rahi-soft)" }}>
          No blocks yet. Add your first block above.
        </div>
      )}
    </div>
  );
}

function AddSlot({ open, onToggle, onPick, small }) {
  return (
    <div className={small ? "py-1" : "py-2"}>
      {!open ? (
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 text-xs py-1.5 rounded border border-dashed hover:bg-white transition"
          style={{ borderColor: "var(--rahi-border)", color: "var(--rahi-soft)" }}
          data-testid="block-add-slot"
        >
          <Plus size={12}/> Add block
        </button>
      ) : (
        <div className="card p-3 grid grid-cols-3 gap-1.5" data-testid="block-picker">
          {BLOCK_LIBRARY.map(({ type, label }) => {
            const Icon = ICONS[type] || Type;
            return (
              <button
                key={type}
                onClick={() => onPick(type)}
                className="flex flex-col items-center gap-1 p-2 rounded hover:bg-[#FFF1ED] text-xs"
                data-testid={`block-add-${type}`}
              >
                <Icon size={16}/>
                <span className="text-[11px]">{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-3">
      <label className="label">{label}</label>
      {children}
      {hint && <p className="hint">{hint}</p>}
    </div>
  );
}

function BlockFields({ type, data, onPatch }) {
  const F = Field;

  if (type === "header") {
    return (
      <div>
        <F label="Logo URL" hint="Leave blank to use the default RAHI logo.">
          <input className="input" value={data.logo_url || ""} onChange={(e) => onPatch({ logo_url: e.target.value })}/>
        </F>
        <F label="Tagline (top-right)">
          <input className="input" value={data.tagline || ""} onChange={(e) => onPatch({ tagline: e.target.value })}/>
        </F>
      </div>
    );
  }
  if (type === "hero") {
    return (
      <div>
        <F label="Headline">
          <input className="input" value={data.headline || ""} onChange={(e) => onPatch({ headline: e.target.value })}/>
        </F>
        <F label="Subhead">
          <textarea className="textarea" rows={2} value={data.subhead || ""} onChange={(e) => onPatch({ subhead: e.target.value })}/>
        </F>
        <F label="Hero image URL (optional)">
          <input className="input" value={data.image_url || ""} onChange={(e) => onPatch({ image_url: e.target.value })}/>
        </F>
        <F label="Image alt text">
          <input className="input" value={data.alt || ""} onChange={(e) => onPatch({ alt: e.target.value })}/>
        </F>
      </div>
    );
  }
  if (type === "text") {
    return (
      <div>
        <F label="Section title (optional)">
          <input className="input" value={data.title || ""} onChange={(e) => onPatch({ title: e.target.value })}/>
        </F>
        <F label="Body" hint="Single newline = line break · Blank line = new paragraph · Use %recipient.name% for personalization.">
          <textarea className="textarea" rows={6} value={data.body || ""} onChange={(e) => onPatch({ body: e.target.value })}/>
        </F>
      </div>
    );
  }
  if (type === "image") {
    return (
      <div>
        <F label="Image URL">
          <input className="input" value={data.src || ""} onChange={(e) => onPatch({ src: e.target.value })}/>
        </F>
        <F label="Alt text"><input className="input" value={data.alt || ""} onChange={(e) => onPatch({ alt: e.target.value })}/></F>
        <F label="Click-through URL (optional)"><input className="input" value={data.link || ""} onChange={(e) => onPatch({ link: e.target.value })}/></F>
        <F label="Caption (optional)"><input className="input" value={data.caption || ""} onChange={(e) => onPatch({ caption: e.target.value })}/></F>
      </div>
    );
  }
  if (type === "stats") {
    const rows = data.rows || [];
    const ensureKey = (r) => r._k || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
    const setRow = (i, patch) => {
      const next = rows.map((r, j) => (j === i ? { ...r, _k: r._k || ensureKey(r), ...patch } : r));
      onPatch({ rows: next });
    };
    const addRow = () => onPatch({ rows: [...rows, { _k: ensureKey({}), number: "", label: "" }] });
    const delRow = (i) => onPatch({ rows: rows.filter((_, j) => j !== i) });
    return (
      <div>
        <F label="Section title (optional)"><input className="input" value={data.title || ""} onChange={(e) => onPatch({ title: e.target.value })}/></F>
        <label className="label">Stats</label>
        {rows.map((r, i) => {
          const key = r._k || `row-${i}-${r.number || ""}-${r.label || ""}`;
          return (
            <div key={key} className="flex gap-2 mb-2">
              <input placeholder="Number" className="input flex-1" value={r.number || ""} onChange={(e) => setRow(i, { number: e.target.value })}/>
              <input placeholder="Label" className="input flex-[2]" value={r.label || ""} onChange={(e) => setRow(i, { label: e.target.value })}/>
              <button className="icon-btn text-red-600" onClick={() => delRow(i)}><Trash2 size={13}/></button>
            </div>
          );
        })}
        <button onClick={addRow} className="btn btn-ghost text-xs"><Plus size={12}/> Add row</button>
        <p className="hint mt-2">Tip: 3 stats look best. The grid stacks on mobile.</p>
      </div>
    );
  }
  if (type === "cta") {
    return (
      <div>
        <F label="Button label"><input className="input" value={data.label || ""} onChange={(e) => onPatch({ label: e.target.value })}/></F>
        <F label="URL"><input className="input" value={data.url || ""} onChange={(e) => onPatch({ url: e.target.value })}/></F>
        <F label="Alignment">
          <select className="input" value={data.alignment || "center"} onChange={(e) => onPatch({ alignment: e.target.value })}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </F>
      </div>
    );
  }
  if (type === "cta_row") {
    const buttons = Array.isArray(data.buttons) ? data.buttons : [];
    const patchBtn = (i, patch) => {
      const next = buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
      onPatch({ buttons: next });
    };
    const removeBtn = (i) => onPatch({ buttons: buttons.filter((_, idx) => idx !== i) });
    const addBtn = () => onPatch({ buttons: [...buttons, { label: "New action", url: "https://", color: "#FF2101" }] });
    const moveBtn = (i, dir) => {
      const j = i + dir;
      if (j < 0 || j >= buttons.length) return;
      const next = [...buttons];
      [next[i], next[j]] = [next[j], next[i]];
      onPatch({ buttons: next });
    };
    return (
      <div>
        <F label="Section title"><input className="input" value={data.title || ""} onChange={(e) => onPatch({ title: e.target.value })} placeholder="Take Action Today"/></F>
        <F label="Tagline (italic quote above buttons)" hint="Leave blank to hide.">
          <textarea className="textarea" rows={2} value={data.tagline || ""} onChange={(e) => onPatch({ tagline: e.target.value })}/>
        </F>
        <div className="mt-3">
          <p className="text-xs font-semibold text-[#57534E] mb-2">Buttons</p>
          {buttons.map((b, i) => (
            <div key={i} className="border border-[#E7E5E4] rounded p-2 mb-2 bg-[#FAFAF9]">
              <div className="flex gap-2 items-center mb-1">
                <input className="input flex-1" placeholder="Label" value={b.label || ""} onChange={(e) => patchBtn(i, { label: e.target.value })}/>
                <input type="color" className="w-9 h-9 rounded border border-[#E7E5E4] cursor-pointer" value={b.color || "#FF2101"} onChange={(e) => patchBtn(i, { color: e.target.value })} title="Button colour"/>
              </div>
              <input className="input mb-1" placeholder="https://…" value={b.url || ""} onChange={(e) => patchBtn(i, { url: e.target.value })}/>
              <div className="flex gap-1 justify-end">
                <button onClick={() => moveBtn(i, -1)} disabled={i===0} className="btn btn-ghost text-xs">↑</button>
                <button onClick={() => moveBtn(i, 1)} disabled={i===buttons.length-1} className="btn btn-ghost text-xs">↓</button>
                <button onClick={() => removeBtn(i)} className="btn btn-ghost text-xs text-red-600">Remove</button>
              </div>
            </div>
          ))}
          <button onClick={addBtn} className="btn btn-ghost text-xs"><Plus size={12}/> Add button</button>
          <p className="hint mt-2">Tip: 4–5 buttons look best. On mobile they stack full-width automatically.</p>
        </div>
      </div>
    );
  }
  if (type === "divider") {
    return <p className="hint">A simple horizontal rule. No options.</p>;
  }
  if (type === "footer") {
    return (
      <div>
        <F label="Organisation name"><input className="input" value={data.org || ""} onChange={(e) => onPatch({ org: e.target.value })}/></F>
        <F label="Address (one line)"><input className="input" value={data.address || ""} onChange={(e) => onPatch({ address: e.target.value })}/></F>
        <p className="hint">The unsubscribe link is added automatically using %recipient.unsubscribe_url%.</p>
      </div>
    );
  }
  if (type === "testimonial") {
    return (
      <div>
        <F label="Quote">
          <textarea className="textarea" rows={4} value={data.quote || ""} onChange={(e) => onPatch({ quote: e.target.value })} placeholder="Share a short, real quote from a volunteer, partner or beneficiary."/>
        </F>
        <F label="Name"><input className="input" value={data.name || ""} onChange={(e) => onPatch({ name: e.target.value })} placeholder="Full name"/></F>
        <F label="Role / designation" hint="e.g., 'Volunteer', 'Program Manager, XYZ NGO', 'CSR Head, Company'."><input className="input" value={data.role || ""} onChange={(e) => onPatch({ role: e.target.value })} placeholder="Volunteer"/></F>
      </div>
    );
  }
  if (type === "raw") {
    return (
      <F label="Raw HTML" hint="Advanced — paste a snippet to insert as-is. Use for custom one-off blocks.">
        <textarea className="textarea font-mono text-xs" rows={8} value={data.html || ""} onChange={(e) => onPatch({ html: e.target.value })}/>
      </F>
    );
  }
  if (type === "social") {
    return (
      <div>
        <F label="Heading (above icons)"><input className="input" value={data.heading || ""} onChange={(e) => onPatch({ heading: e.target.value })} placeholder="Get regular updates on our work"/></F>
        <F label="Facebook URL"><input className="input" value={data.facebook || ""} onChange={(e) => onPatch({ facebook: e.target.value })} placeholder="https://facebook.com/…"/></F>
        <F label="X (Twitter) URL"><input className="input" value={data.x || ""} onChange={(e) => onPatch({ x: e.target.value })} placeholder="https://x.com/…"/></F>
        <F label="LinkedIn URL"><input className="input" value={data.linkedin || ""} onChange={(e) => onPatch({ linkedin: e.target.value })} placeholder="https://linkedin.com/…"/></F>
        <F label="Instagram URL"><input className="input" value={data.instagram || ""} onChange={(e) => onPatch({ instagram: e.target.value })} placeholder="https://instagram.com/…"/></F>
        <F label="YouTube URL"><input className="input" value={data.youtube || ""} onChange={(e) => onPatch({ youtube: e.target.value })} placeholder="https://youtube.com/…"/></F>
        <F label="Icon size (px)" hint="Default 40 — larger sizes look bigger in Gmail's rendering.">
          <input className="input" type="number" min={24} max={72} value={data.size || 40} onChange={(e) => onPatch({ size: Number(e.target.value) || 40 })}/>
        </F>
        <p className="hint">Leave a URL blank to hide that icon. Recipients will see the icons in their inbox as clickable links.</p>
      </div>
    );
  }
  return null;
}
